<template>
  <a-drawer
    :title="$t('paiementHistory.paymentDetail')"
    :visible="visible"
    :width="560"
    @close="$emit('close')"
    destroy-on-close
  >
    <a-spin :spinning="loading">
      <template v-if="detail">
        <a-descriptions bordered :column="1" size="small">
          <a-descriptions-item :label="$t('paiementHistory.date')">
            {{ formatDate(detail.paidAt) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.montant')">
            <span class="amount-cell">{{ (detail.totalToPay || 0).toFixed(3) }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('paiementHistory.paymentMode')">
            <a-tag :color="detail.paymentMode === 'immediate' ? 'blue' : 'purple'">
              {{ detail.paymentMode === 'immediate' ? $t('paiementHistory.immediate') : $t('paiementHistory.scheduled') }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('paiementHistory.status')">
            <a-tag :color="statusColor(displayStatusKey(detail))">{{ $t('paiementHistory.statuses.' + displayStatusKey(detail)) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('paiementHistory.validationStatus')">
            <a-tag :color="valStatusColor(detail.validationStatus)">{{ $t('paiementHistory.validationStatuses.' + detail.validationStatus) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detail.validationStatus === 'rejected' && detail.rejectedBy"
            :label="$t('paiementHistory.rejectedBy')"
          >
            {{ detail.rejectedBy }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detail.validationStatus === 'rejected' && validationDecisionDate(detail)"
            :label="$t('paiementHistory.rejectedAt')"
          >
            {{ formatDateTime(validationDecisionDate(detail)) }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detail.validationStatus === 'validated' && detail.validatedBy"
            :label="$t('paiementHistory.validatedBy')"
          >
            {{ detail.validatedBy }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detail.validationStatus === 'validated' && validationDecisionDate(detail)"
            :label="$t('paiementHistory.validatedAt')"
          >
            {{ formatDateTime(validationDecisionDate(detail)) }}
          </a-descriptions-item>
          <a-descriptions-item v-if="detail.receiptNumber" :label="$t('decaissement.receiptNumber')">
            {{ detail.receiptNumber }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('paiementHistory.createdBy')">
            {{ detail.createdBy }}
          </a-descriptions-item>
        </a-descriptions>

        <a-alert
          v-if="detail.validationStatus === 'rejected'"
          type="error"
          show-icon
          class="mb-3"
        >
          <template slot="message">
            {{ $t('reconciliation.rejectionReason') }}
          </template>
          <template slot="description">
            {{ detail.rejectionReason || $t('paiementHistory.rejectionReasonMissing') }}
          </template>
        </a-alert>

        <template v-if="detail._debtSummary">
          <a-divider orientation="left"><a-icon type="link" /> {{ $t("dette.debtInfo") }}</a-divider>
          <a-descriptions bordered :column="1" size="small" class="mb-3">
            <a-descriptions-item :label="$t('paiementHistory.debtLabel')">
              {{ detail._debtSummary.label }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.debtType')">
              {{ debtTypeLabel(detail._debtSummary.debtType) }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.totalAmount')">
              <span class="amount-cell">{{ (detail._debtSummary.totalAmount || 0).toFixed(3) }}</span>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('paiementHistory.debtRepaymentStatus')">
              <a-tag :color="debtStatusColor(detail._debtSummary.status)">{{ debtStatusLabel(detail._debtSummary.status) }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.beneficiaryType')">
              {{ debtBeneficiaryTypeLabel(detail._debtSummary.beneficiaryType) }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.beneficiary')">
              {{ detail._debtSummary.beneficiaryName || "—" }}
            </a-descriptions-item>
          </a-descriptions>

          <div v-if="detail._debtRemainingSchedules && detail._debtRemainingSchedules.length" class="schedule-subtitle">
            {{ $t("paiementHistory.debtRemainingInstallments") }}
          </div>
          <a-table
            v-if="detail._debtRemainingSchedules && detail._debtRemainingSchedules.length"
            :columns="debtRemainingColumns"
            :data-source="detail._debtRemainingSchedules"
            :pagination="false"
            size="small"
            row-key="_id"
            bordered
            class="mb-3"
          >
            <template slot="debtDepLabel" slot-scope="text">{{ text || "—" }}</template>
            <template slot="debtDue" slot-scope="text">{{ formatDate(text) }}</template>
            <template slot="debtAmt" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
            <template slot="debtSt" slot-scope="text">
              <a-tag :color="text === 'paid' ? 'green' : text === 'pending' ? 'orange' : 'default'">{{ text }}</a-tag>
            </template>
          </a-table>
          <p v-else class="text-muted small mb-3">{{ $t("paiementHistory.debtNoRemainingInstallments") }}</p>
        </template>

        <div v-for="(sp, spIdx) in displayPaymentSplits" :key="spIdx" class="payment-split-detail mb-3">
          <div class="split-detail-title">
            {{ $t('saisiePaiement.paymentLine', { n: spIdx + 1 }) }}
            <template v-if="displayPaymentSplits.length === 1">
              — <a-icon :type="methodIcon(sp.method)" class="mr-1" />
              {{ methodLabel(sp.method) }}
            </template>
          </div>
          <a-descriptions bordered :column="1" size="small">
            <a-descriptions-item v-if="displayPaymentSplits.length > 1" :label="$t('paiementHistory.method')">
              <a-icon :type="methodIcon(sp.method)" class="mr-1" />
              {{ methodLabel(sp.method) }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('recette.montant')">
              <span class="amount-cell">{{ (sp.amount != null ? sp.amount : detail.totalToPay || 0).toFixed(3) }}</span>
            </a-descriptions-item>
            <a-descriptions-item v-if="sp.method === 'cash'" :label="$t('paiementHistory.fundSource')">
              {{ $t('decaissement.myCaisse') }}
            </a-descriptions-item>
            <a-descriptions-item v-else :label="$t('paiementHistory.fundSource')">
              {{ $t('decaissement.caisseBank') }}
            </a-descriptions-item>
            <a-descriptions-item v-if="sp._caisse" :label="$t('decaissement.cashName')">
              {{ sp._caisse.name }}
            </a-descriptions-item>
            <a-descriptions-item v-if="sp._bankAccount" :label="$t('decaissement.caisseBank')">
              {{ sp._bankAccount.name }}
            </a-descriptions-item>
          </a-descriptions>

          <template v-if="sp.methodDetails && sp.methodDetails.cheques && sp.methodDetails.cheques.length">
            <a-divider orientation="left"><a-icon type="credit-card" /> {{ $t('decaissement.paymentMethod.cheque') }}</a-divider>
            <a-table :columns="chequeColumns" :data-source="sp.methodDetails.cheques" :pagination="false" size="small" :row-key="(r, i) => 'ch-' + spIdx + '-' + i" bordered>
              <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
            </a-table>
          </template>
          <template v-if="sp.methodDetails && sp.methodDetails.traites && sp.methodDetails.traites.length">
            <a-divider orientation="left"><a-icon type="file-protect" /> {{ $t('decaissement.paymentMethod.traite') }}</a-divider>
            <a-table :columns="traiteColumns" :data-source="sp.methodDetails.traites" :pagination="false" size="small" :row-key="(r, i) => 'tr-' + spIdx + '-' + i" bordered>
              <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
            </a-table>
          </template>
          <template v-if="sp.methodDetails && sp.methodDetails.virements && sp.methodDetails.virements.length">
            <a-divider orientation="left"><a-icon type="bank" /> {{ $t('decaissement.paymentMethod.virement') }}</a-divider>
            <a-table :columns="virementColumns" :data-source="sp.methodDetails.virements" :pagination="false" size="small" :row-key="(r, i) => 'vi-' + spIdx + '-' + i" bordered>
              <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
            </a-table>
          </template>
        </div>

        <!-- Related depenses (provider invoices only) -->
        <template v-if="showRelatedInvoices">
          <a-divider orientation="left"><a-icon type="file-text" /> {{ $t('paiementHistory.relatedInvoices') }}</a-divider>
          <a-list size="small" :data-source="providerDepenses" bordered>
            <a-list-item slot="renderItem" slot-scope="dep">
              <a-list-item-meta>
                <template slot="title">
                  {{ dep.label || dep.invoiceNumber || '—' }}
                </template>
                <template slot="description">
                  {{ dep.beneficiaryName }} — {{ dep.invoiceNumber || dep.reference || '' }}
                </template>
              </a-list-item-meta>
            </a-list-item>
          </a-list>
        </template>

        <template v-if="depenseAttachmentItems.length">
          <a-divider orientation="left"><a-icon type="paper-clip" /> {{ $t("factures.attachments") }}</a-divider>
          <div class="attachment-grid mb-3">
            <div v-for="item in depenseAttachmentItems" :key="item.id" class="attachment-item">
              <div class="attachment-main">
                <a-icon type="paper-clip" class="attachment-icon" />
                <div class="attachment-texts">
                  <div class="attachment-name" :title="attachmentDisplayName(item.key)">
                    {{ attachmentDisplayName(item.key) }}
                  </div>
                  <div class="attachment-source">
                    {{ item.depLabel }}
                  </div>
                </div>
              </div>
              <a :href="resourceUrl(item.key)" target="_blank" rel="noopener noreferrer" class="attachment-action">
                {{ $t("action.voir") }}
              </a>
            </div>
          </div>
        </template>

        <!-- Schedules: this payment + other plannifications on same depenses -->
        <template v-if="hasAnySchedules">
          <a-divider orientation="left"><a-icon type="schedule" /> {{ $t('paiementHistory.schedules') }}</a-divider>
          <template v-if="detail._schedules && detail._schedules.length">
            <div class="schedule-subtitle">{{ $t('paiementHistory.schedulesThisPayment') }}</div>
            <a-table
              :columns="scheduleColumns"
              :data-source="detail._schedules"
              :pagination="false"
              size="small"
              row-key="_id"
              bordered
              class="mb-3"
            >
              <template slot="date" slot-scope="text">{{ formatDate(text) }}</template>
              <template slot="amt" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
              <template slot="status" slot-scope="text">
                <a-tag :color="text === 'paid' ? 'green' : text === 'pending' ? 'orange' : 'default'">{{ text }}</a-tag>
              </template>
            </a-table>
          </template>
          <template v-if="detail._otherSchedules && detail._otherSchedules.length">
            <div class="schedule-subtitle">{{ $t('paiementHistory.otherSchedules') }}</div>
            <p v-if="!detail._schedules || !detail._schedules.length" class="schedule-hint text-muted">
              {{ $t('paiementHistory.otherSchedulesHint') }}
            </p>
            <a-table
              :columns="otherScheduleColumns"
              :data-source="detail._otherSchedules"
              :pagination="false"
              size="small"
              row-key="_id"
              bordered
            >
              <template slot="depLabel" slot-scope="text">{{ text || '—' }}</template>
              <template slot="date" slot-scope="text">{{ formatDate(text) }}</template>
              <template slot="amt" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
              <template slot="status" slot-scope="text">
                <a-tag :color="text === 'paid' ? 'green' : text === 'pending' ? 'orange' : 'default'">{{ text }}</a-tag>
              </template>
            </a-table>
          </template>
        </template>
      </template>
      <a-empty v-else-if="!loading" />
    </a-spin>
  </a-drawer>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import { fetchPaymentDetail } from "@/services/api/compV3/paymentEntry";

export default {
  name: "PaymentDetailDrawer",
  props: {
    visible: { type: Boolean, default: false },
    paymentId: { type: String, default: null },
  },
  data() {
    return { detail: null, loading: false };
  },
  computed: {
    ...mapState(["settings"]),
    displayPaymentSplits() {
      const p = this.detail;
      if (!p) return [];
      return p.splitPayments && p.splitPayments.length ? p.splitPayments : [];
    },
    showRelatedInvoices() {
      const deps = this.detail?._depenses;
      return Array.isArray(deps) && deps.some((d) => d.beneficiaryType === "provider");
    },
    providerDepenses() {
      const deps = this.detail?._depenses;
      if (!Array.isArray(deps)) return [];
      return deps.filter((d) => d.beneficiaryType === "provider");
    },
    hasAnySchedules() {
      const d = this.detail;
      return (d?._schedules?.length > 0) || (d?._otherSchedules?.length > 0);
    },
    depenseAttachmentItems() {
      const deps = this.detail?._depenses;
      if (!Array.isArray(deps)) return [];
      const items = [];
      deps.forEach((dep) => {
        const keys = Array.isArray(dep?.attachmentKeys) ? dep.attachmentKeys : [];
        keys.forEach((key, idx) => {
          const depLabel = dep?.label || dep?.invoiceNumber || dep?.reference || "—";
          items.push({
            id: `${dep?._id || depLabel}-${idx}-${key}`,
            key,
            depLabel,
          });
        });
      });
      return items;
    },
    chequeColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        { title: this.$t("decaissement.chequeNum"), dataIndex: "num", key: "num" },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dateEchance", key: "date", customRender: fmtDue },
        { title: this.$t("decaissement.chequeTitulaire"), dataIndex: "titulaire", key: "tit" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
    traiteColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        { title: this.$t("decaissement.chequeNum"), dataIndex: "num", key: "num" },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dateEchance", key: "date", customRender: fmtDue },
        { title: this.$t("decaissement.traiteRib"), dataIndex: "rib", key: "rib" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
    virementColumns() {
      return [
        { title: this.$t("decaissement.virementNum"), dataIndex: "num", key: "num" },
        { title: this.$t("decaissement.chequeTitulaire"), dataIndex: "titulaire", key: "tit" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
    scheduleColumns() {
      return [
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "due", scopedSlots: { customRender: "date" } },
        { title: this.$t("recette.montant"), dataIndex: "amount", key: "amt", scopedSlots: { customRender: "amt" } },
        { title: this.$t("paiementHistory.status"), dataIndex: "status", key: "st", scopedSlots: { customRender: "status" } },
      ];
    },
    otherScheduleColumns() {
      return [
        { title: this.$t("paiementHistory.expenseOrInvoice"), dataIndex: "_depenseLabel", key: "dep", scopedSlots: { customRender: "depLabel" }, ellipsis: true },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "due", scopedSlots: { customRender: "date" } },
        { title: this.$t("recette.montant"), dataIndex: "amount", key: "amt", scopedSlots: { customRender: "amt" } },
        { title: this.$t("paiementHistory.status"), dataIndex: "status", key: "st", scopedSlots: { customRender: "status" } },
      ];
    },
    debtRemainingColumns() {
      return [
        { title: this.$t("paiementHistory.expenseOrInvoice"), dataIndex: "_depenseLabel", key: "dde", scopedSlots: { customRender: "debtDepLabel" }, ellipsis: true },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "ddu", scopedSlots: { customRender: "debtDue" } },
        { title: this.$t("recette.montant"), dataIndex: "amount", key: "dam", scopedSlots: { customRender: "debtAmt" } },
        { title: this.$t("paiementHistory.status"), dataIndex: "status", key: "dst", scopedSlots: { customRender: "debtSt" } },
      ];
    },
  },
  watch: {
    paymentId(id) { if (id) this.loadDetail(id); else this.detail = null; },
    visible(v) { if (v && this.paymentId) this.loadDetail(this.paymentId); },
  },
  methods: {
    debtTypeLabel(t) {
      const key = "dette." + t;
      const tr = this.$t(key);
      return tr !== key ? tr : t || "—";
    },
    debtBeneficiaryTypeLabel(bt) {
      if (bt === "associate") return this.$t("dette.associate");
      if (bt === "debiteur") return this.$t("dette.debiteur");
      return bt || "—";
    },
    debtStatusLabel(s) {
      const map = { unpaid: "fournisseur.unpaid", partial: "fournisseur.partial", paid: "fournisseur.paid" };
      const key = map[s];
      return key ? this.$t(key) : s || "—";
    },
    debtStatusColor(s) {
      if (s === "paid") return "green";
      if (s === "partial") return "orange";
      return "red";
    },
    formatDate(d) { return d ? moment(d).format("DD/MM/YYYY") : "—"; },
    formatDateTime(d) {
      return d ? moment(d).format("DD/MM/YYYY HH:mm") : "—";
    },
    validationDecisionDate(record) {
      if (!record) return null;
      if (record.validationStatus === "validated") {
        return record.validatedAt || record.updatedAt || null;
      }
      if (record.validationStatus === "rejected") {
        return record.rejectedAt || record.updatedAt || null;
      }
      return null;
    },
    statusColor(s) {
      if (s === "awaiting_validation") return "orange";
      return s === "completed" ? "green" : s === "pending" ? "orange" : "red";
    },
    displayStatusKey(record) {
      if (
        record &&
        record.validationStatus === "pending_validation" &&
        record.caisseDebitApplied === false
      ) {
        return "awaiting_validation";
      }
      return record && record.status ? record.status : "pending";
    },
    valStatusColor(s) { return s === "validated" ? "green" : s === "rejected" ? "red" : "orange"; },
    methodIcon(m) {
      const map = { cash: "wallet", cheque: "credit-card", virement: "bank", traite: "file-protect", prelevement: "sync", tpe: "tablet" };
      return map[m] || "dollar";
    },
    methodLabel(m) {
      const key = "decaissement.paymentMethod." + m;
      const t = this.$t(key);
      return t !== key ? t : m || "—";
    },
    resourceUrl(filePath) {
      if (!filePath) return "";
      const tpl = this.settings && this.settings.resourcesUrl;
      return tpl ? tpl.replace("FILE_PATH", filePath) : "";
    },
    attachmentDisplayName(filePath) {
      if (!filePath) return "—";
      const chunks = String(filePath).split("/");
      return chunks[chunks.length - 1] || filePath;
    },
    async loadDetail(id) {
      this.loading = true;
      try {
        const res = await fetchPaymentDetail(id);
        this.detail = res.data;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.amount-cell { font-weight: 600; color: #1890ff; }
.schedule-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 8px;
}
.schedule-hint {
  font-size: 12px;
  margin-bottom: 8px;
}
.split-detail-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #262626;
}
.payment-split-detail {
  padding-bottom: 8px;
  border-bottom: 1px dashed #f0f0f0;
}
.payment-split-detail:last-child {
  border-bottom: none;
}
.attachment-links-list a {
  word-break: break-all;
}
.attachment-grid {
  display: grid;
  gap: 8px;
}
.attachment-item {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fafafa;
}
.attachment-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.attachment-icon {
  color: #1890ff;
}
.attachment-texts {
  min-width: 0;
}
.attachment-name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-source {
  color: #8c8c8c;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-action {
  white-space: nowrap;
}
</style>
